import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Artist } from '../types/artist';
import { artistApi } from '../services/artistApi';
import TrackUpload from './TrackUpload';
import TrackManager from './TrackManager';
import { usePageTracking } from '../hooks/usePageTracking';
import './GetFeedbackTab.css';

interface GetFeedbackTabProps {
  initialView?: 'tracks' | 'upload';
  selectedTrackPublicId?: string;
}

const GetFeedbackTab: React.FC<GetFeedbackTabProps> = ({ initialView = 'tracks', selectedTrackPublicId }) => {
  const { user, isAuthenticated } = useAuth();
  
  // Track page view
  usePageTracking(initialView === 'tracks' ? 'My Tracks' : 'Upload Track');
  const [artists, setArtists] = useState<Artist[]>([]);
  const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'tracks' | 'upload'>(initialView);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Add refresh trigger

  const loadArtists = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      const userArtists = await artistApi.getArtistsByUserId(user.id);
      setArtists(userArtists);
    } catch (err) {
      console.error('Failed to load artists:', err);
      setError(err instanceof Error ? err.message : 'Failed to load artists');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (isAuthenticated && user) {
      loadArtists();
    }
  }, [isAuthenticated, user, loadArtists]);

  useEffect(() => {
    // Sync activeView with initialView prop
    setActiveView(initialView);
  }, [initialView]);

  useEffect(() => {
    // Auto-select the first artist if available
    if (artists.length > 0 && !selectedArtist) {
      setSelectedArtist(artists[0]);
    }
  }, [artists, selectedArtist]);

  const handleTrackCreated = () => {
    // Refresh the track list
    setRefreshTrigger(prev => prev + 1); // Trigger refresh
    // Navigate to my-tracks view
    window.history.pushState(null, '', '/my-tracks');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  if (!isAuthenticated) {
    return (
      <div className="get-feedback-tab">
        <div className="auth-required">
          <h2>Get Feedback</h2>
          <p>Please log in to upload tracks and receive feedback from the community.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="get-feedback-tab">
        <div className="loading">Loading your artist profiles...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="get-feedback-tab">
        <div className="error">
          <p>Error: {error}</p>
          <button className="retry-button" onClick={loadArtists}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (artists.length === 0) {
    return (
      <div className="get-feedback-tab">
        <div className="no-artists">
          <h2>Get Feedback</h2>
          <p>You need to create an artist profile before uploading tracks.</p>
          <p>Go to your Account tab to create your first artist profile.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="get-feedback-tab">
      {/* Artist Selection */}
      {artists.length > 1 && (
        <div className="artist-selector">
          <label htmlFor="artist-select">Select Artist Profile:</label>
          <select
            id="artist-select"
            value={selectedArtist?.id || ''}
            onChange={(e) => {
              const artistId = parseInt(e.target.value);
              const artist = artists.find(a => a.id === artistId);
              setSelectedArtist(artist || null);
            }}
          >
            {artists.map(artist => (
              <option key={artist.id} value={artist.id}>
                {artist.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {selectedArtist && (
        <>
          {/* Content Based on Active View */}
          <div className="feedback-content">
            {activeView === 'upload' && selectedArtist.id ? (
              <TrackUpload
                artistId={selectedArtist.id}
                karma={selectedArtist.karma}
                isPremium={selectedArtist.entitlements?.premium ?? false}
                authorizedUserId={selectedArtist.authorizedUserId}
                onTrackCreated={handleTrackCreated}
              />
            ) : (
              <TrackManager
                artistId={selectedArtist.id}
                userId={selectedArtist.authorizedUserId}
                showFeedback={true}
                refreshTrigger={refreshTrigger}
                selectedTrackPublicId={selectedTrackPublicId}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default GetFeedbackTab;